import React, { useState, useEffect } from 'react';
import {
    Layout, Typography, Card, Upload, Button, Table, InputNumber,
    Form, Space, Divider, message, Row, Col, Alert, Steps, Result,
    Modal, Radio, Tag
} from 'antd';
import {
    UploadOutlined, DownloadOutlined, CalculatorOutlined,
    SettingOutlined, FileExcelOutlined, ClearOutlined,
    HomeOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import PublicHeader from '../../components/layout/PublicHeader';
import PublicFooter from '../../components/layout/PublicFooter';
import { useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

const GradeConverter = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [fileList, setFileList] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);

    // Configuration State
    // Configuration State
    const [subjectConfigs, setSubjectConfigs] = useState({}); // { '语文': 100, '数学': 120 }
    const [schoolStage, setSchoolStage] = useState('primary'); // primary, junior, senior
    const [gradeConfig, setGradeConfig] = useState([
        { grade: 'A', percent: 90, color: '#f5222d' }, // Red
        { grade: 'B', percent: 80, color: '#fa8c16' }, // Orange
        { grade: 'C', percent: 70, color: '#faad14' }, // Gold
        { grade: 'D', percent: 60, color: '#1890ff' }, // Blue
        { grade: 'E', percent: 0, color: '#52c41a' }, // Green
    ]);
    const [templateModalVisible, setTemplateModalVisible] = useState(false);

    // Subject Templates
    const subjectTemplates = {
        'primary': {
            name: '小学模板',
            subjects: ['语文', '数学', '英语', '科学', '道德与法治', '体育', '音乐', '美术']
        },
        'junior': {
            name: '初中模板',
            subjects: ['语文', '数学', '英语', '物理', '化学', '生物', '历史', '地理', '道德与法治', '体育']
        },
        'senior': {
            name: '高中模板',
            subjects: ['语文', '数学', '英语', '物理', '化学', '生物', '历史', '地理', '思想政治', '体育', '信息技术', '通用技术']
        },
        'universal': {
            name: '通用全科模板',
            subjects: ['语文', '数学', '英语', '物理', '化学', '生物', '历史', '地理', '政治', '科学', '体育', '音乐', '美术']
        }
    };

    // Handle file upload
    const handleUpload = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }); // Array of arrays

                if (jsonData.length < 2) {
                    message.error('Excel文件内容为空或格式不正确');
                    return;
                }

                // Headers are the first row
                const headers = jsonData[0];
                const rows = jsonData.slice(1).map((row, index) => {
                    const rowData = { key: index };
                    headers.forEach((header, i) => {
                        rowData[header] = row[i];
                    });
                    return rowData;
                });

                setColumns(headers.map(h => ({ title: h, dataIndex: h, key: h })));
                setTableData(rows);

                // Identify potential subject columns (heuristic: not standard info columns)
                const excludedCols = ['姓名', '学号', '座位号', '班级', '学校', '手机', '电话', 'name', 'id', 'no', 'code', 'phone', '自动评级', '得分率'];
                const detectedSubjects = headers.filter(h =>
                    !excludedCols.some(ex => String(h).toLowerCase().includes(ex))
                );

                // Auto-detect Stage based on subjects
                let detectedStage = 'primary';
                if (detectedSubjects.some(s => ['通用技术', '信息技术', '政治', '思想政治'].some(k => s.includes(k))) && detectedSubjects.some(s => s.includes('物理'))) {
                    detectedStage = 'senior';
                } else if (detectedSubjects.some(s => ['物理', '化学', '生物', '历史', '地理'].some(k => s.includes(k)))) {
                    detectedStage = 'junior';
                }

                setSchoolStage(detectedStage);

                // Initialize subject configs with defaults based on detected stage
                const initialConfigs = {};
                detectedSubjects.forEach(sub => {
                    let score = 100;
                    if (['语文', '数学', '英语'].some(k => sub.includes(k))) {
                        if (detectedStage === 'junior') score = 120;
                        if (detectedStage === 'senior') score = 150;
                    }
                    initialConfigs[sub] = score;
                });

                setSubjectConfigs(initialConfigs);
                setCurrentStep(1);
                message.success(`文件解析成功，已自动识别为${detectedStage === 'primary' ? '小学' : detectedStage === 'junior' ? '初中' : '高中'}模式`);
            } catch (error) {
                console.error('File parse error:', error);
                message.error('文件解析失败，请确保是有效的Excel文件');
            }
        };
        reader.readAsArrayBuffer(file);
        return false; // Prevent upload
    };

    // Open Template Modal
    const downloadTemplate = () => {
        setTemplateModalVisible(true);
    };

    // Actual Download Logic
    const handleDownloadTemplate = (type) => {
        const selectedTemplate = subjectTemplates[type];
        const headers = ['姓名', '学号', ...selectedTemplate.subjects, '总分'];

        // Generate dummy data based on subjects count
        const generateScores = () => selectedTemplate.subjects.map(() => Math.floor(Math.random() * 20 + 80));

        const data = [
            ['张三', '1001', ...generateScores(), ''],
            ['李四', '1002', ...generateScores(), ''],
            ['王五', '1003', ...generateScores(), ''],
        ];

        const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

        // Set column widths
        const wscols = headers.map(() => ({ wch: 10 }));
        ws['!cols'] = wscols;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, selectedTemplate.name);
        XLSX.writeFile(wb, `${selectedTemplate.name}.xlsx`);
        setTemplateModalVisible(false);
        message.success(`已下载${selectedTemplate.name}`);
    };

    // Update a specific grade config
    const updateGradeConfig = (index, field, value) => {
        const newConfig = [...gradeConfig];
        newConfig[index][field] = value;
        setGradeConfig(newConfig);
    };

    // Calculate Grades
    const calculateGrades = () => {
        const subjects = Object.keys(subjectConfigs);
        if (subjects.length === 0) {
            message.error('没有配置任何科目');
            return;
        }

        setLoading(true);
        setTimeout(() => {
            const processedData = tableData.map(row => {
                const newRow = { ...row };

                subjects.forEach(subject => {
                    const score = parseFloat(row[subject]);
                    const fullScore = subjectConfigs[subject] || 100;

                    if (!isNaN(score)) {
                        const percentage = (score / fullScore) * 100;
                        // Find the matching grade
                        let grade = '';
                        let color = '';
                        for (const config of gradeConfig) {
                            if (percentage >= config.percent) {
                                grade = config.grade;
                                color = config.color;
                                break;
                            }
                        }

                        // Add grade columns
                        newRow[`${subject}等级`] = grade;
                        // newRow[`${subject}得分率`] = ((score / fullScore) * 100).toFixed(1) + '%'; 
                    } else {
                        newRow[`${subject}等级`] = '';
                    }
                });
                return newRow;
            });

            // Rebuild columns
            let baseColumns = columns.filter(c => !c.title.includes('等级'));

            // Add grading columns for each subject
            subjects.forEach(subject => {
                baseColumns.push({
                    title: `${subject}等级`,
                    dataIndex: `${subject}等级`,
                    key: `${subject}_grade`,
                    render: (text) => {
                        const config = gradeConfig.find(c => c.grade === text);
                        return <Text strong style={{ color: config?.color }}>{text}</Text>;
                    }
                });
            });

            setColumns(baseColumns);
            setTableData(processedData);
            setLoading(false);
            setCurrentStep(2);
            message.success('等级计算完成！');
        }, 500);
    };

    // Export Results
    const exportResults = () => {
        // Prepare data for export (remove key)
        const exportData = tableData.map(({ key, ...rest }) => rest);
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "成绩评级结果");
        XLSX.writeFile(wb, `成绩评级结果_${new Date().getTime()}.xlsx`);
    };

    const resetAll = () => {
        setFileList([]);
        setTableData([]);
        setColumns([]);
        setCurrentStep(0);
    };

    return (
        <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
            <PublicHeader />

            <Content style={{ padding: '0 24px', marginTop: 64 }}>
                <div style={{ maxWidth: 1200, margin: '24px auto' }}>

                    {/* Header Section */}
                    <div style={{ textAlign: 'center', marginBottom: 40 }}>
                        <Title level={2}>成绩等级转换工具</Title>
                        <Paragraph type="secondary">
                            无需登录，无需联网，直接在浏览器中批量转换成绩等级。数据安全，隐私无忧。
                        </Paragraph>
                    </div>

                    <Card bordered={false} style={{ marginBottom: 24, borderRadius: 8 }}>
                        <Steps current={currentStep} style={{ marginBottom: 40 }}>
                            <Step title="导入成绩" description="上传Excel文件" icon={<UploadOutlined />} />
                            <Step title="设置规则" description="设定满分和等级标准" icon={<SettingOutlined />} />
                            <Step title="生成与导出" description="查看结果并保存" icon={<DownloadOutlined />} />
                        </Steps>

                        {/* Step 1: Upload */}
                        {currentStep === 0 && (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                <Upload.Dragger
                                    beforeUpload={handleUpload}
                                    showUploadList={false}
                                    accept=".xlsx, .xls"
                                    height={200}
                                >
                                    <p className="ant-upload-drag-icon">
                                        <FileExcelOutlined style={{ color: '#1890ff' }} />
                                    </p>
                                    <p className="ant-upload-text">点击或拖拽Excel文件到此区域上传</p>
                                    <p className="ant-upload-hint">
                                        支持 .xlsx, .xls 格式。第一行为表头。
                                    </p>
                                </Upload.Dragger>
                                <div style={{ marginTop: 24 }}>
                                    <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
                                        下载示例模板
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Configuration */}
                        {currentStep === 1 && (
                            <div>
                                <Row gutter={24}>
                                    <Col xs={24} md={12}>
                                        <Card title="基础设置" size="small" style={{ marginBottom: 16 }}>
                                            <div style={{ marginBottom: 16 }}>
                                                <Text strong>快速设置满分：</Text>
                                                <Radio.Group
                                                    value={schoolStage}
                                                    onChange={(e) => {
                                                        const stage = e.target.value;
                                                        setSchoolStage(stage);

                                                        const newConfigs = { ...subjectConfigs };
                                                        Object.keys(newConfigs).forEach(sub => {
                                                            if (stage === 'junior' && ['语文', '数学', '英语'].some(k => sub.includes(k))) {
                                                                newConfigs[sub] = 120;
                                                            } else if (stage === 'senior' && ['语文', '数学', '英语'].some(k => sub.includes(k))) {
                                                                newConfigs[sub] = 150;
                                                            } else {
                                                                newConfigs[sub] = 100;
                                                            }
                                                        });
                                                        setSubjectConfigs(newConfigs);
                                                    }}
                                                    buttonStyle="solid"
                                                >
                                                    <Radio.Button value="primary">小学 (100分)</Radio.Button>
                                                    <Radio.Button value="junior">初中 (语数英120)</Radio.Button>
                                                    <Radio.Button value="senior">高中 (语数英150)</Radio.Button>
                                                </Radio.Group>
                                            </div>

                                            <Divider style={{ margin: '12px 0' }} />

                                            <Form layout="vertical" size="small">
                                                <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}>
                                                    {Object.keys(subjectConfigs).length === 0 && <Alert message="未检测到科目，请检查Excel表头" type="warning" />}
                                                    <Row gutter={[16, 8]}>
                                                        {Object.keys(subjectConfigs).map(subject => (
                                                            <Col span={8} key={subject}>
                                                                <Form.Item label={subject} style={{ marginBottom: 0 }}>
                                                                    <InputNumber
                                                                        min={1}
                                                                        value={subjectConfigs[subject]}
                                                                        onChange={(val) => {
                                                                            setSubjectConfigs(prev => ({ ...prev, [subject]: val }));
                                                                        }}
                                                                        style={{ width: '100%' }}
                                                                        addonAfter="分"
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                </div>
                                            </Form>
                                        </Card>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Card title="等级阈值设置" size="small">
                                            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
                                                当得分率大于等于设定值时，判定为该等级
                                            </Paragraph>
                                            {gradeConfig.map((config, index) => (
                                                <Row key={config.grade} align="middle" gutter={8} style={{ marginBottom: 12 }}>
                                                    <Col span={3}>
                                                        <Text strong style={{ fontSize: 16, color: config.color }}>{config.grade}</Text>
                                                    </Col>
                                                    <Col span={2}>
                                                        <Text>≥</Text>
                                                    </Col>
                                                    <Col span={8}>
                                                        <InputNumber
                                                            min={0}
                                                            max={100}
                                                            value={config.percent}
                                                            onChange={(val) => updateGradeConfig(index, 'percent', val)}
                                                            formatter={value => `${value}%`}
                                                            parser={value => value.replace('%', '')}
                                                        />
                                                    </Col>
                                                    <Col span={11}>
                                                        <Text type="secondary">
                                                            (得分率)
                                                        </Text>
                                                    </Col>
                                                </Row>
                                            ))}
                                        </Card>
                                    </Col>
                                </Row>

                                <Divider />

                                <div style={{ textAlign: 'center' }}>
                                    <Space size="large">
                                        <Button onClick={resetAll}>重新上传</Button>
                                        <Button
                                            type="primary"
                                            icon={<CalculatorOutlined />}
                                            onClick={calculateGrades}
                                            loading={loading}
                                            size="large"
                                        >
                                            开始计算等级
                                        </Button>
                                    </Space>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Result & Export */}
                        {currentStep === 2 && (
                            <div>
                                <Result
                                    status="success"
                                    title="计算完成！"
                                    subTitle={`共处理 ${tableData.length} 条数据`}
                                    extra={[
                                        <Button type="primary" key="export" icon={<DownloadOutlined />} onClick={exportResults}>
                                            导出结果到Excel
                                        </Button>,
                                        <Button key="reset" icon={<ClearOutlined />} onClick={resetAll}>
                                            处理新文件
                                        </Button>,
                                    ]}
                                />

                                <Divider orientation="left">数据预览</Divider>

                                <Table
                                    dataSource={tableData.slice(0, 5)} // Only show first 5 rows
                                    columns={columns}
                                    pagination={false}
                                    size="small"
                                    bordered
                                    scroll={{ x: true }}
                                />
                                <Button type="link" onClick={() => setTableData(tableData)}>
                                    查看全部数据...
                                </Button>
                            </div>
                        )}
                        {/* Template Selection Modal */}
                        <Modal
                            title="选择成绩模板类型"
                            open={templateModalVisible}
                            onCancel={() => setTemplateModalVisible(false)}
                            footer={null}
                        >
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Button block size="large" onClick={() => handleDownloadTemplate('primary')}>
                                    小学模板 (语数英科等)
                                </Button>
                                <Button block size="large" onClick={() => handleDownloadTemplate('junior')}>
                                    初中模板 (语数英物化生政史地等)
                                </Button>
                                <Button block size="large" onClick={() => handleDownloadTemplate('senior')}>
                                    高中模板 (包含技术类科目)
                                </Button>
                                <Button block size="large" onClick={() => handleDownloadTemplate('universal')}>
                                    通用全科模板
                                </Button>
                            </Space>
                        </Modal>
                    </Card>

                    <div style={{ textAlign: 'center' }}>
                        <Button type="link" icon={<HomeOutlined />} onClick={() => navigate('/')}>
                            返回首页
                        </Button>
                    </div>
                </div>
            </Content>

            <PublicFooter />
        </Layout>
    );
};

export default GradeConverter;
